import type {
  CollectionName,
  DatabaseName,
  DeleteDocParams,
  DeleteSubDocParams,
  LiveDocParams,
  LiveQueryV2Params,
  OnFirebaseErrorFn,
  QueryCollectionGroupParams,
  QueryV2Params,
  SubQueryDocParams,
  SubQueryParams,
  UpdateDocParams,
  UpdateSubDocParams,
  WriteDocParams,
  WriteSubDocParams,
} from './types';

import { DEFAULT_AUTH_TENANT } from '@snowit/system/useTenants/utils';

import { app, auth, db, dbLogs, functions } from './config';
import QueryBuilder from './QueryBuilder';
import { mergeFilters, queryCollection } from './utils';

import * as Auth from 'firebase/auth';
import * as Database from 'firebase/database';
import * as Fire from 'firebase/firestore';
import * as Funcs from 'firebase/functions';
import * as Storage from 'firebase/storage';

const FirebaseSDKFactory = (
  name: DatabaseName = 'default',
  onError: OnFirebaseErrorFn = () => {},
) => {
  let database: Fire.Firestore;
  switch (name) {
    case 'logs':
      database = dbLogs;
      break;
    default:
      database = db;
  }

  const query = async <T extends Record<string, any>>(params: QueryV2Params): Promise<T[]> => {
    const { query: q, debug } = queryCollection({
      database,
      collectionName: params.collectionName,
      filters: params.filters,
      logger: params.logger,
    });
    try {
      const snap = await Fire.getDocs(q);
      const ret: T[] = [];
      snap.forEach((ref) => {
        const entry = {
          ...ref.data(),
          uid: ref.id,
        } as unknown as T;
        ret.push(entry);
      });
      return ret;
    } catch (error: any) {
      console.error(error, { error, params, debug });
      onError(error, 'query', params.collectionName);
      return [];
    }
  };

  const collectionGroup = async <T extends Record<string, any>>(
    params: QueryCollectionGroupParams,
  ): Promise<T[]> => {
    const { query: q, debug } = queryCollection({
      database,
      collectionName: params.collectionName,
      filters: params.filters,
      logger: params.logger,
      isCollectionGroup: true,
    });
    try {
      const snap = await Fire.getDocs(q);
      const ret: T[] = [];
      snap.forEach((ref) => {
        const entry = {
          ...ref.data(),
          uid: ref.id,
          $parentUID: ref.ref.parent.parent?.id,
        } as unknown as T;
        ret.push(entry);
      });
      return ret;
    } catch (error: any) {
      console.error(error, { error, params, debug });
      onError(error, 'collectionGroup', params.collectionName);
      return [];
    }
  };

  const loadMore = async <T extends Record<string, any>>(
    params: LiveQueryV2Params,
  ): Promise<{ data: T[]; next?: Function }> => {
    const { collectionName, filters = [], logger } = params;
    const { query: q, debug } = queryCollection({ database, collectionName, filters, logger });
    try {
      const snap = await Fire.getDocs(q);
      const lastVisible = snap.docs[snap.docs.length - 1];
      const ret: T[] = [];
      snap.forEach((ref) => {
        ret.push({ ...ref.data(), uid: ref.id } as unknown as T);
      });
      return {
        data: ret as T[],
        next:
          ret.length !== 0
            ? () => {
                return loadMore({
                  ...params,
                  filters: mergeFilters(filters, (query) => query.startAfter(lastVisible)),
                  logger,
                });
              }
            : undefined,
      };
    } catch (error: any) {
      console.error(error, { error, params, debug });
      onError(error, 'load_more', params.collectionName);
      return { data: [], next: undefined };
    }
  };

  const doc = async <T extends Record<string, any>>(
    params: LiveDocParams,
  ): Promise<T | undefined> => {
    try {
      const { collectionName, id } = params;
      const q = new QueryBuilder(database, collectionName).queryDoc(id);
      const ref = await Fire.getDoc(q);
      if (!ref.exists()) return undefined;
      return { ...ref.data(), uid: ref.id } as unknown as T;
    } catch (error: any) {
      console.error(error, { error, params });
      onError(error, 'doc', params.collectionName);
      if (params.throwOnError) throw error;
      return undefined;
    }
  };

  const count = async (params: LiveQueryV2Params): Promise<number> => {
    const { collectionName, filters = [], logger } = params;
    const { query, debug } = queryCollection({
      database,
      collectionName,
      filters,
      disableSort: true,
      logger,
    });
    try {
      const snap = await Fire.getCountFromServer(query);
      return snap.data().count;
    } catch (error: any) {
      console.error(error, { error, params, debug });
      onError(error, 'count', params.collectionName);
      return 0;
    }
  };

  const sum = async (params: LiveQueryV2Params & { field: string }): Promise<number> => {
    const { collectionName, filters = [], field, logger } = params;
    const { query: q, debug } = queryCollection({
      database,
      collectionName,
      filters,
      disableSort: true,
      logger,
    });
    try {
      const snap = await Fire.getAggregateFromServer(q, {
        result: Fire.sum(field),
      });
      return snap.data().result;
    } catch (error: any) {
      console.error(error, { error, params, debug });
      onError(error, 'sum', params.collectionName);
      return 0;
    }
  };

  const queryLive = async (
    params: LiveQueryV2Params,
    callback: (snapshot: Fire.QuerySnapshot) => void,
  ) => {
    const { collectionName, filters = [], logger } = params;
    const { query: q } = queryCollection({ database, collectionName, filters, logger });
    return Fire.onSnapshot(q, callback);
    // return unsub;
  };

  const subcollection = async <T extends Record<string, any>>(
    params: SubQueryParams,
  ): Promise<T[]> => {
    try {
      const q = Fire.collection(
        database,
        params.collectionName,
        params.id,
        params.subcollectionName,
      );
      const snap = await Fire.getDocs(q);
      const ret: T[] = [];
      snap.forEach((ref) => {
        ret.push({ ...ref.data(), uid: ref.id } as unknown as T);
      });
      return ret;
    } catch (error: any) {
      console.error(error, { error, params });
      onError(error, 'subcollection', params.collectionName, params.subcollectionName);
      return [];
    }
  };

  const subcollectionDoc = async <T extends Record<string, any>>(
    params: SubQueryDocParams,
  ): Promise<T | undefined> => {
    try {
      const q = new QueryBuilder(database, params.collectionName).querySubDoc(
        params.id,
        params.subcollectionName,
        params.subId,
      );

      const ref = await Fire.getDoc(q);
      if (!ref.exists()) return undefined;
      return { ...ref.data(), uid: ref.id } as unknown as T;
    } catch (error: any) {
      console.error(error, { error, params });
      onError(error, 'subcollection_doc', params.collectionName, params.subcollectionName);
      return undefined;
    }
  };

  const write = async <T extends Record<string, any>>(params: WriteDocParams<T>) => {
    const { collectionName, id, data } = params;
    if (id) {
      await Fire.setDoc(Fire.doc(database, collectionName, id), data);
      return id;
    }
    const snap = await Fire.addDoc(Fire.collection(database, collectionName), data);
    return snap.id;
  };

  const writeSub = async <T extends Record<string, any>>(params: WriteSubDocParams<T>) => {
    const { collectionName, id, data } = params;
    const { subcollectionName, subId } = params;
    const docRef = Fire.doc(database, collectionName, id);
    if (subId) {
      await Fire.setDoc(Fire.doc(docRef, subcollectionName, subId), data);
      return subId;
    }
    const snap = await Fire.addDoc(Fire.collection(docRef, subcollectionName), data);
    return snap.id;
  };

  const update = async <T extends Record<string, any>>(params: UpdateDocParams<T>) => {
    const { collectionName, id, data } = params;
    const q = Fire.doc(database, collectionName, id);
    await Fire.updateDoc(q, data);
    return id;
  };

  const updateSub = async <T extends Record<string, any>>(params: UpdateSubDocParams<T>) => {
    const { collectionName, id, data } = params;
    const { subcollectionName, subId } = params;
    const q = Fire.doc(database, collectionName, id, subcollectionName, subId);
    await Fire.updateDoc(q, data);
    return id;
  };

  const deleteDoc = async (params: DeleteDocParams) => {
    const { collectionName, id } = params;
    const q = Fire.doc(database, collectionName, id);
    const snap = await Fire.deleteDoc(q);
    return snap;
  };

  const deleteSub = async (params: DeleteSubDocParams) => {
    const { collectionName, id, subcollectionName, subId } = params;
    const q = Fire.doc(database, collectionName, id, subcollectionName, subId);
    const snap = await Fire.deleteDoc(q);
    return snap;
  };

  const randomId = async (collectionName: CollectionName): Promise<string> => {
    const q = Fire.doc(Fire.collection(database, collectionName));
    return q.id;
  };

  const httpsCallable = (callable: string) =>
    Funcs.httpsCallable(functions, callable, { timeout: 300000 });

  const signIn = (
    email: string,
    password: string,
    authTenant?: string,
  ): Promise<Auth.UserCredential> => {
    auth.tenantId = authTenant && authTenant !== DEFAULT_AUTH_TENANT ? authTenant : null;
    return Auth.signInWithEmailAndPassword(auth, email, password);
  };

  const signOut = (): Promise<void> => Auth.signOut(auth);

  const signInAs = (_token: string) => Auth.signInWithCustomToken(auth, _token);

  const sendSignInLinkToEmail = (email: string, url: string) =>
    Auth.sendSignInLinkToEmail(auth, email, { url, handleCodeInApp: true });

  const resetPassword = async (email: string, url: string) =>
    Auth.sendPasswordResetEmail(auth, email, { url, handleCodeInApp: true });

  const onAuthChange = (func: (user: Auth.User | null) => any): Auth.Unsubscribe =>
    Auth.onAuthStateChanged(auth, func);

  const getToken = async (): Promise<string | undefined> => {
    if (!auth.currentUser) return Promise.resolve(undefined);
    return auth.currentUser.getIdToken();
  };

  const realtimeSubscribe = async (path: string) => {
    const realtimeDB = Database.getDatabase();
    const ref = Database.ref(realtimeDB, path);
    return Database.onValue(ref, (snapshot) => snapshot.val());
  };

  const realtimeGet = async (path: string) => {
    const realtimeDB = Database.getDatabase();
    const ref = Database.ref(realtimeDB, path);
    const res = await Database.get(ref);
    if (res.exists()) return res.val();
    return undefined;
  };

  const realtimeWrite = async (path: string) => {
    const realtimeDB = Database.getDatabase();
    const ref = Database.ref(realtimeDB, path);
    await Database.set(ref, {
      foo: 'bar',
    });
    // const res = await Database.get(Database.child(ref, path));
    // if (res.exists()) return res.val();
    // return undefined;
  };

  const readFile = async (pathToFile: string, bucketName?: string): Promise<string> => {
    const bucket = Storage.getStorage(app, bucketName);
    const fileRef = Storage.ref(bucket, pathToFile);
    return Storage.getDownloadURL(fileRef);
  };

  /** Reads a text file with the signed-in user's credentials (subject to Storage rules) */
  const readText = async (pathToFile: string, bucketName?: string): Promise<string> => {
    const bucket = Storage.getStorage(app, bucketName);
    const bytes = await Storage.getBytes(Storage.ref(bucket, pathToFile));
    return new TextDecoder().decode(bytes);
  };

  const readFiles = async (path: string, bucketName?: string): Promise<string[]> => {
    const bucket = Storage.getStorage(app, bucketName);
    const listRef = Storage.ref(bucket, path);
    const res = await Storage.listAll(listRef);
    return res.items.map((fileRef) => fileRef.name);
  };

  const putFile = async (
    file: File,
    path: string,
    bucketName?: string,
  ): Promise<string | undefined> => {
    try {
      const bucket = Storage.getStorage(app, bucketName);
      const fileRef = Storage.ref(bucket, path);
      await Storage.uploadBytes(fileRef, file);
      const url = await readFile(path, bucketName);
      return url;
    } catch {
      return undefined;
    }
  };

  const makeGeopoint = (latitude: number, longitude: number) =>
    new Fire.GeoPoint(latitude, longitude);

  const makeTimestamp = (epoch: number) => new Fire.Timestamp(epoch, 0);

  return {
    count,
    database,
    delete: deleteDoc,
    deleteSub,
    doc,
    loadMore,
    query,
    queryLive,
    subcollection,
    subcollectionDoc,
    sum,
    update,
    updateSub,
    write,
    writeSub,
    randomId,
    httpsCallable,
    signIn,
    signOut,
    signInAs,
    sendSignInLinkToEmail,
    resetPassword,
    onAuthChange,
    getToken,
    realtimeGet,
    realtimeWrite,
    realtimeSubscribe,
    readFiles,
    readFile,
    readText,
    putFile,
    makeGeopoint,
    makeTimestamp,
    collectionGroup,
  };
};

export default FirebaseSDKFactory;
